import './TestList.css';
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { BottomSheet } from 'react-spring-bottom-sheet';
import 'react-spring-bottom-sheet/dist/style.css';
import { useNavigate } from 'react-router-dom';
import { useSetRecoilState, useRecoilValue, useRecoilState } from 'recoil';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TestItem from './Item/TestItem';
import TestItemSkeleton from './Item/TestItemSkeleton';
import { navigationBarState } from '../../../states/NavigationBar';
import { LoggedInState } from '../../../states/LoggedIn';
import { accessTokenState } from '../../../states/Token';

function TestList() {
    const navigate = useNavigate();
    const setNavigationBarState = useSetRecoilState(navigationBarState);
    const loggedIn = useRecoilValue(LoggedInState);
    const [, setTokenState] = useRecoilState(accessTokenState);
    const [fetchError, setFetchError] = useState(null);
    const [testList, setTestList] = useState(null);
    const [currentTest, setCurrentTest] = useState(null);
    const [pressedMenuId, setPressedMenuId] = useState(null);
    const pressTimeoutRef = useRef(null);

    // Pull to Refresh
    const [pullDistance, setPullDistance] = useState(0);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const containerRef = useRef(null);
    const startYRef = useRef(0);
    const isPullingRef = useRef(false);
    const PULL_THRESHOLD = 80;

    // Rolling Emoji
    const examEmojis = ['📝', '✏️', '📚', '🎯', '💯', '🏆', '⭐', '📖', '🎓', '✨'];
    const [currentEmoji, setCurrentEmoji] = useState(examEmojis[0]);
    const emojiIntervalRef = useRef(null);

    useEffect(() => {
        const shouldRoll = pullDistance >= PULL_THRESHOLD || isRefreshing;

        if (shouldRoll) {
            if (!emojiIntervalRef.current) {
                emojiIntervalRef.current = setInterval(() => {
                    setCurrentEmoji(prev => {
                        const currentIndex = examEmojis.indexOf(prev);
                        const randomIndex = Math.floor(Math.random() * examEmojis.length);
                        // 같은 이모지가 연속으로 나오지 않게
                        if (randomIndex === currentIndex) {
                            return examEmojis[(randomIndex + 1) % examEmojis.length];
                        }
                        return examEmojis[randomIndex];
                    });
                }, 200);
            }
        } else {
            if (emojiIntervalRef.current) {
                clearInterval(emojiIntervalRef.current);
                emojiIntervalRef.current = null;
            }
        }

        return () => {
            if (emojiIntervalRef.current) {
                clearInterval(emojiIntervalRef.current);
                emojiIntervalRef.current = null;
            }
        };
    }, [pullDistance, isRefreshing]);

    const handleMenuPress = useCallback((itemId, path) => {
        setPressedMenuId(itemId);
        if (pressTimeoutRef.current) {
            clearTimeout(pressTimeoutRef.current);
        }
        pressTimeoutRef.current = setTimeout(() => {
            setPressedMenuId(null);
            navigate(path);
        }, 80);
    }, [navigate]);

    const levelTestInfo = loggedIn?.user?.level_test;
    const hasCompletedLevelTest = levelTestInfo?.status === 'EVALUATED';

    const menuItems = useMemo(() => [
        !levelTestInfo && {
            id: 'level-test',
            title: '레벨테스트',
            description: '내 레벨에 맞는 시험 찾기',
            icon: '🎯',
            badge: '무료',
            badgeType: 'primary',
            path: '/test-store/level-test'
        },
        {
            id: 'test-store',
            title: '시험 신청하기',
            description: '실전 시험에 도전하기',
            icon: '📝',
            badge: hasCompletedLevelTest ? '추천' : null,
            badgeType: 'secondary',
            path: '/test-store'
        }
    ].filter(Boolean), [levelTestInfo, hasCompletedLevelTest]);

    const testOrder = (test) => {
        switch (test.status) {
            case "READY":
            case "TESTING":
                return 0;
            case "WAITING":
                return 1;
            case "EXPIRED":
                return 3;
            default:
                return 2;
        }
    };

    const refreshUserInfo = useCallback(async () => {
        try {
            const response = await axios.get('/users/me/');
            if (response.data) {
                setTokenState(prevState => {
                    if (!prevState) return prevState;
                    return {
                        ...prevState,
                        user: { ...prevState.user, ...response.data }
                    };
                });
            }
        } catch (error) {
            console.error('Failed to refresh user info:', error);
        }
    }, [setTokenState]);

    const fetchTestList = useCallback((isRefresh = false) => {
        if (!isRefresh) {
            setFetchError(null);
            setTestList(null);
        }

        return axios.get('/tests/').then(async (response) => {
            let tests = response.data;
            tests.sort((left, right) => {
                let leftOrder = testOrder(left);
                let rightOrder = testOrder(right);
                if (leftOrder === rightOrder) {
                    return left.end_time - right.end_time;
                }
                return leftOrder - rightOrder;
            });
            setTestList(tests);

            const levelTest = tests.find(test => test.academy_class_type === 'L');
            const serverLevelTestStatus = levelTest?.status || null;
            const userLevelTestStatus = loggedIn?.user?.level_test?.status || null;

            if (serverLevelTestStatus !== userLevelTestStatus) {
                await refreshUserInfo();
            }
        }).catch((error) => {
            setFetchError(error);
        });
    }, [loggedIn?.user?.level_test?.status, refreshUserInfo]);

    const handleRefresh = useCallback(async () => {
        setIsRefreshing(true);
        const startTime = Date.now();

        try {
            await fetchTestList(true);
        } finally {
            // 최소 1초는 롤링 유지
            const elapsed = Date.now() - startTime;
            const remaining = Math.max(0, 1000 - elapsed);

            setTimeout(() => {
                setIsRefreshing(false);
                setPullDistance(0);
            }, remaining);
        }
    }, [fetchTestList]);

    const handleTouchStart = useCallback((e) => {
        if (isRefreshing) return;
        const scrollTop = window.scrollY || document.documentElement.scrollTop;
        if (scrollTop <= 0) {
            startYRef.current = e.touches[0].clientY;
            isPullingRef.current = true;
        }
    }, [isRefreshing]);

    const handleTouchMove = useCallback((e) => {
        if (!isPullingRef.current || isRefreshing) return;

        const currentY = e.touches[0].clientY;
        const diff = currentY - startYRef.current;

        if (diff > 0) {
            // 저항감 있는 당김 효과 (로그 함수 사용)
            const resistance = Math.min(diff * 0.5, PULL_THRESHOLD * 1.5);
            setPullDistance(resistance);

            if (diff > 10) {
                e.preventDefault();
            }
        }
    }, [isRefreshing]);

    const handleTouchEnd = useCallback(() => {
        if (!isPullingRef.current) return;
        isPullingRef.current = false;

        if (pullDistance >= PULL_THRESHOLD && !isRefreshing) {
            handleRefresh();
        } else {
            setPullDistance(0);
        }
    }, [pullDistance, isRefreshing, handleRefresh]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        container.addEventListener('touchstart', handleTouchStart, { passive: true });
        container.addEventListener('touchmove', handleTouchMove, { passive: false });
        container.addEventListener('touchend', handleTouchEnd, { passive: true });

        return () => {
            container.removeEventListener('touchstart', handleTouchStart);
            container.removeEventListener('touchmove', handleTouchMove);
            container.removeEventListener('touchend', handleTouchEnd);
        };
    }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

    useEffect(() => {
        window.scrollTo(0, 0);
        setNavigationBarState({
            title: 'Exams',
            'left-item': { type: 'logout' },
            'right-item': { type: 'settings' },
        });
        fetchTestList();
    }, []);

    const renderTestList = () => {
        if (fetchError) {
            return (
                <div className="tests-error">
                    <div className="tests-error-text">{fetchError.message}</div>
                </div>
            );
        }

        if (testList === null) {
            return (
                <div className="tests-list">
                    <TestItemSkeleton />
                    <TestItemSkeleton />
                    <TestItemSkeleton />
                </div>
            );
        }

        if (testList.length === 0) {
            return (
                <div className="tests-empty">
                    <div className="tests-empty-text">진행가능한 시험이 없습니다</div>
                </div>
            );
        }

        return (
            <div className="tests-list">
                {testList.map((test) => (
                    <TestItem
                        key={test.id}
                        test={test}
                        onStart={() => setCurrentTest(test)}
                        onContinue={() => navigate(`/tests/${test.id}`)}
                        onResult={() => navigate(`/tests/${test.id}/score-report`)}
                    />
                ))}
            </div>
        );
    };

    const pullProgress = Math.min(pullDistance / PULL_THRESHOLD, 1);
    const showRefreshIndicator = pullDistance > 0 || isRefreshing;

    return (
        <div className="tests-page" ref={containerRef}>
            {/* Pull to Refresh Indicator */}
            {showRefreshIndicator && (
                <div
                    className="ptr-indicator"
                    style={{
                        height: isRefreshing ? PULL_THRESHOLD : pullDistance,
                        opacity: isRefreshing ? 1 : pullProgress,
                    }}
                >
                    <div
                        className={`ptr-emoji ${isRefreshing ? 'refreshing' : ''}`}
                        style={{
                            transform: `scale(${0.5 + pullProgress * 0.5})`,
                        }}
                    >
                        {currentEmoji}
                    </div>
                </div>
            )}

            <div
                className="tests-content-wrapper"
                style={{
                    transform: showRefreshIndicator
                        ? `translateY(${isRefreshing ? PULL_THRESHOLD : pullDistance}px)`
                        : 'translateY(0)',
                    transition: isPullingRef.current ? 'none' : 'transform 0.3s ease-out',
                }}
            >
                <div className="tests-container">
                    {/* Header */}
                    <div className="tests-header">
                        <div className="tests-header-title">{loggedIn.user.nickname}님, 안녕하세요</div>
                        <div className="tests-header-subtitle">{loggedIn.user.username}</div>
                    </div>

                    {/* 시험 신청 섹션 - 개인회원만 */}
                    {loggedIn.user.type === 'I' && menuItems.length > 0 && (
                        <div className="tests-menu-list">
                                {menuItems.map((item) => (
                                    <div
                                        key={item.id}
                                        className={`tests-menu-button ${pressedMenuId === item.id ? 'pressed' : ''}`}
                                        onClick={() => handleMenuPress(item.id, item.path)}
                                    >
                                        <div className="tests-menu-button-left">
                                            <span className="tests-menu-button-icon">{item.icon}</span>
                                            <div className="tests-menu-button-content">
                                                <span className="tests-menu-button-text">{item.title}</span>
                                                <span className="tests-menu-button-desc">{item.description}</span>
                                            </div>
                                        </div>
                                        <div className="tests-menu-button-right">
                                            {item.badge && (
                                                <span className={`tests-menu-button-badge ${item.badgeType === 'secondary' ? 'secondary' : ''}`}>
                                                    {item.badge}
                                                </span>
                                            )}
                                            <span className="tests-menu-button-arrow">
                                                <ChevronRightIcon />
                                            </span>
                                        </div>
                                    </div>
                                ))}
                        </div>
                    )}

                    {/* 나의 시험 섹션 */}
                    <div className="tests-section">
                        <div className="tests-section-label">
                            나의 시험{testList && testList.length > 0 ? ` (${testList.length})` : ''}
                        </div>
                        {renderTestList()}
                    </div>
                </div>

                {/* Bottom Sheet */}
                <BottomSheet
                    style={{ maxWidth: '568px' }}
                    onDismiss={() => setCurrentTest(null)}
                    open={currentTest != null}
                >
                    {currentTest && (
                        <div className="tests-sheet">
                            <div className="tests-sheet-title">시험을 시작할까요?</div>
                            <div className="tests-sheet-desc">제한시간이 지나면 자동으로 제출됩니다</div>
                            <div className="tests-sheet-info">
                                <div className="tests-sheet-stat">
                                    <div className="tests-sheet-stat-value">
                                        {currentTest.question_sheet.number_of_questions}
                                    </div>
                                    <div className="tests-sheet-stat-label">문항</div>
                                </div>
                                <div className="tests-sheet-stat">
                                    <div className="tests-sheet-stat-value">
                                        {Math.round(currentTest.question_sheet.time_limit / 60)}
                                    </div>
                                    <div className="tests-sheet-stat-label">분</div>
                                </div>
                                <div className="tests-sheet-stat">
                                    <div className="tests-sheet-stat-value">
                                        {currentTest.question_sheet.total_score}
                                    </div>
                                    <div className="tests-sheet-stat-label">점</div>
                                </div>
                            </div>
                            <button
                                className="tests-sheet-button"
                                onClick={() => navigate(`/tests/${currentTest.id}`)}
                            >
                                시작하기
                            </button>
                        </div>
                    )}
                </BottomSheet>
            </div>
        </div>
    );
}

export default TestList;
